import { Link } from "react-router-dom";
import { useCallback, useContext, useState, useEffect, useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import Swal from "sweetalert2";

import { PencilIcon, TrashIcon, } from "@heroicons/react/24/outline";

// Context
import SnackbarContext from "../../../context/assessment/SnackbarContext";
import LoaderContext from "../../../context/assessment/LoaderContext";

// Helper
import errorHandler from "../../../helper/assessment/errorHandler";
import logRender from "../../../helper/assessment/logRender";

// Controller
import IntrayEmail from "../../../controller/assessment/assessment_system/intray_email";

// Component
import TableFilter from "../../../components/assessment/assessment_system/TableFilter";
import Table from "../../../components/assessment/assessment_system/Table";

function ListEmail() {
  const { setMessage } = useContext(LoaderContext);
  const { setNotif } = useContext(SnackbarContext);
  const [list, setList] = useState<TIntrayEmail[]>([]);
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    logRender({ type: "page", name: "intray_email/ListEmail" });
  }, []);

  const getList = useCallback(async () => {
    try {
      setMessage("Fetch Intray Email");
      const iec = new IntrayEmail();
      const res = await iec.list();
      setList(res.data.list);
      setMessage("");
    } catch (error) {
      setMessage("");
      const errorMessage = errorHandler(error);
      setNotif({ type: "error", message: errorMessage });
    }
  }, [setNotif, setMessage]);

  useEffect(() => {
    getList();
  }, [getList]);

  const handleDeleteList = (id: number) => {
    const arrFilter = [...list].filter((v) => v.id !== id);
    setList(arrFilter);
  };

  return (
    <div className="w-full flex flex-col space-y-0.5">
      <div className="flex flex-col md:flex-row items-start md:items-center">
        <Link
          to={`create`}
          className="block w-auto mb-2 md:m-0 px-4 py-2 bg-green-500 hover:bg-green-700 text-white rounded font-semibold text-sm"
        >
          <div className="flex">
            <PencilIcon className="w-5 h-5 mr-2 stroke-white fill-tranparent" aria-hidden="true" />
            Create
          </div>
        </Link>
        <div className="w-full md:w-auto ml-auto flex">
          <TableFilter search={setSearch} />
        </div>
      </div>
      <div>
        <IntrayEmailList list={list} handleDeleteList={handleDeleteList} search={search} />
      </div>
    </div>
  );
}

function IntrayEmailList(props: { list: TIntrayEmail[]; handleDeleteList: (id: number) => void; search: string }) {
  const setNotif = useContext(SnackbarContext).setNotif;
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const confirmDelete = useCallback(
    async (email: TIntrayEmail) => {
      try {
        const confirm = await Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        });
        if (confirm.isConfirmed) {
          const iec = new IntrayEmail();
          await iec.delete(email.id.toString());
          props.handleDeleteList(email.id);
          setNotif({ type: "success", message: "intray email deleted" });
        }
      } catch (error) {
        const errorMessage = errorHandler(error);
        setNotif({ type: "error", message: errorMessage });
      }
    },
    [props, setNotif]
  );

  const columns = useMemo(
    () => [
      {
        id: "mobile",
        Header: "Subject",
        accessor: "subject",
        show: isMobile,
        Cell: (props: { row: { original: TIntrayEmail } }) => {
          return (
            <Link to={`${props.row.original.id}`} className="text-blue-500 hover:text-blue-800">
              {props.row.original.subject || "NULL"}
            </Link>
          );
        },
      },
      {
        id: "subject",
        classHeader: "text-left px-2",
        Header: "Subject",
        classDivHeader: "flex items-center",
        accessor: "subject",
        show: !isMobile,
        Cell: (props: { row: { original: TIntrayEmail } }) => {
          return (
            <Link to={`${props.row.original.id}`} className="text-blue-500 hover:text-blue-800">
              {props.row.original.subject || "NULL"}
            </Link>
          );
        },
      },
      {
        id: "date",
        classHeader: "text-left px-2",
        Header: "Date",
        classDivHeader: "flex items-center",
        accessor: "date",
        show: !isMobile,
        Cell: (props: { row: { original: TIntrayEmail } }) => {
          return <span>{props.row.original.date || "NULL"}</span>;
        },
      },
      {
        id: "action_delete",
        classHeader: "w-[30px] text-white",
        Header: () => {
          return <TrashIcon className="h-5" />;
        },
        classDivHeader: "flex justify-center",
        accessor: "id",
        show: !isMobile,
        disableSortBy: true,
        className: "text-white bg-red-500 hover:bg-red-800",
        Cell: (props: { row: { original: TIntrayEmail } }) => {
          return (
            <button className="flex justify-center" onClick={() => confirmDelete(props.row.original)}>
              <TrashIcon className="h-5" />
            </button>
          );
        },
      },
    ],
    [isMobile, confirmDelete]
  );

  return <Table columns={columns} list={props.list} search={props.search} />;
}


export default ListEmail;
